import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import {
  Upload, FileText, ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle,
  Loader2, Users, RefreshCw, UserPlus, UserCheck, SkipForward,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_LABELS = {
  first_name: 'First Name', last_name: 'Last Name', phone: 'Phone', email: 'Email',
  birthday: 'Birthday', company: 'Company', tags: 'Tags', notes: 'Notes', source: 'Source',
};
const STD_FIELDS = ['first_name', 'last_name', 'phone', 'email', 'birthday', 'company', 'tags', 'notes', 'source'];
const SOURCE_PRESETS = ['Project Broadcast', 'Google Contacts', 'Phone Contacts', 'CRM', 'Spreadsheet', 'Other'];

export const ContactImportDialog = ({ open, onOpenChange, folders = [], onImported }) => {
  const { getAuthHeaders } = useAuth();
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const [step, setStep] = useState('upload'); // upload|map|review|confirm|progress|done
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [session, setSession] = useState(null);
  const [mapping, setMapping] = useState({});
  const [duplicateRule, setDuplicateRule] = useState('skip');
  const [sourceSel, setSourceSel] = useState('__default__');
  const [customSource, setCustomSource] = useState('');
  const [addTag, setAddTag] = useState('');
  const [folder, setFolder] = useState('General');

  const [analysis, setAnalysis] = useState(null);
  const [job, setJob] = useState(null); // live job status from /status

  const resolvedSource = sourceSel === '__default__' ? '' : (sourceSel === '__custom__' ? customSource.trim() : sourceSel);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => () => stopPolling(), []);

  const reset = () => {
    stopPolling();
    setStep('upload'); setBusy(false); setError('');
    setSession(null); setMapping({}); setDuplicateRule('skip');
    setSourceSel('__default__'); setCustomSource(''); setAddTag(''); setFolder('General');
    setAnalysis(null); setJob(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const close = (v) => { onOpenChange(v); if (!v) { stopPolling(); setTimeout(reset, 200); } };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please choose a .csv file. You can export one from Project Broadcast, Google Contacts, or a spreadsheet.');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/contacts/import/upload`, fd, {
        headers: { ...getAuthHeaders().headers },
      });
      setSession(res.data);
      setMapping(res.data.suggested_mapping || {});
      setStep('map');
    } catch (err) {
      setError(err.response?.data?.detail || 'We could not read that file. Please try a standard CSV export.');
    } finally {
      setBusy(false);
    }
  };

  const setTarget = (header, target) => {
    setMapping((m) => ({
      ...m,
      [header]: target === 'custom'
        ? { target: 'custom', custom_name: m[header]?.custom_name || header }
        : { target },
    }));
  };
  const setCustomName = (header, name) => {
    setMapping((m) => ({ ...m, [header]: { target: 'custom', custom_name: name } }));
  };

  const runAnalyze = async () => {
    setBusy(true); setError('');
    try {
      const res = await axios.post(`${API}/contacts/import/analyze`, {
        session_id: session.session_id,
        mapping,
        duplicate_rule: duplicateRule,
        source: resolvedSource,
        add_tag: addTag.trim(),
      }, getAuthHeaders());
      setAnalysis(res.data);
      setStep('review');
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong analyzing the file.');
    } finally {
      setBusy(false);
    }
  };

  const pollStatus = (jobId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/contacts/import/status/${jobId}`, getAuthHeaders());
        setJob(res.data);
        if (res.data.status === 'completed') {
          stopPolling();
          setStep('done');
          onImported?.();
        } else if (res.data.status === 'failed') {
          stopPolling();
          setError(res.data.error || 'The import hit a problem. You can resume it to continue from where it stopped.');
        }
      } catch (err) {
        // transient; keep polling
      }
    }, 1500);
  };

  const runImport = async () => {
    setStep('progress');
    setError('');
    setJob({ status: 'processing', processed: 0, total: session.total, imported: 0, updated: 0, skipped: 0, invalid: 0, failed: 0, percent: 0 });
    try {
      const res = await axios.post(`${API}/contacts/import/start`, {
        session_id: session.session_id,
        mapping,
        duplicate_rule: duplicateRule,
        source: resolvedSource || 'CSV Import',
        add_tag: addTag.trim(),
        folder,
        filename: session.filename,
      }, getAuthHeaders());
      setJob(res.data);
      onImported?.(); // refresh history so the running job shows up immediately
      pollStatus(res.data.job_id);
    } catch (err) {
      setError(err.response?.data?.detail || 'We could not start the import. Please try again.');
      setStep('confirm');
    }
  };

  const resumeImport = async () => {
    if (!job?.job_id) return;
    setError('');
    try {
      const res = await axios.post(`${API}/contacts/import/resume/${job.job_id}`, {}, getAuthHeaders());
      setJob(res.data);
      pollStatus(job.job_id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not resume the import.');
    }
  };

  const progressPct = job?.percent ?? (job?.total ? Math.round((job.processed / job.total) * 100) : 0);
  const sampleRow = session?.sample_rows?.[0] || {};

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto p-0" data-testid="contact-import-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b sticky top-0 bg-background z-10">
          <DialogTitle className="flex items-center gap-2 text-pink-600">
            <Upload className="w-5 h-5" /> Import Contacts
          </DialogTitle>
        </DialogHeader>

        <div className="px-5 py-4">
          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900 p-3 text-sm text-red-700 dark:text-red-300" data-testid="import-error">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* STEP: UPLOAD */}
          {step === 'upload' && (
            <div className="space-y-4 text-center">
              <div>
                <div className="mx-auto w-14 h-14 rounded-2xl bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center mb-3">
                  <FileText className="w-7 h-7 text-pink-600" />
                </div>
                <h3 className="font-outfit font-bold text-lg">Import Your Contacts</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Upload a CSV file containing your contacts. Export them from Project Broadcast, Google Contacts, spreadsheets, CRMs, or other platforms and bring them into BOB.
                </p>
              </div>
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} data-testid="import-file-input" />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="w-full py-8 rounded-2xl border-2 border-dashed border-pink-300 dark:border-pink-800 hover:border-pink-500 hover:bg-pink-50 dark:hover:bg-pink-900/20 transition-colors flex flex-col items-center gap-2 text-pink-600"
                data-testid="import-choose-file-btn"
              >
                {busy ? <Loader2 className="w-8 h-8 animate-spin" /> : <Upload className="w-8 h-8" />}
                <span className="font-medium">{busy ? 'Reading file…' : 'Tap to choose a CSV file'}</span>
                <span className="text-xs text-muted-foreground">Standard .csv files</span>
              </button>
            </div>
          )}

          {/* STEP: MAP */}
          {step === 'map' && session && (
            <div className="space-y-4">
              <div>
                <h3 className="font-outfit font-bold text-lg">Map Your Fields</h3>
                <p className="text-sm text-muted-foreground">
                  We matched your columns automatically. Adjust any that look off. Choose <b>Don't Import</b> to skip a column, or <b>Custom Field</b> to keep extra info.
                </p>
              </div>
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <FileText className="w-3.5 h-3.5" /> {session.filename} · {session.total} rows
              </div>
              <div className="space-y-2.5">
                {(session.headers || []).map((h) => {
                  const m = mapping[h] || { target: 'skip' };
                  const sample = (sampleRow[h] || '').toString().slice(0, 40);
                  return (
                    <div key={h} className="rounded-xl border p-3 bg-slate-50 dark:bg-slate-900/40" data-testid={`map-row-${h}`}>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{h}</p>
                          {sample && <p className="text-[11px] text-muted-foreground truncate">e.g. {sample}</p>}
                        </div>
                        <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
                        <Select value={m.target} onValueChange={(v) => setTarget(h, v)}>
                          <SelectTrigger className="w-[150px] shrink-0" data-testid={`map-select-${h}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {STD_FIELDS.map((f) => <SelectItem key={f} value={f}>{FIELD_LABELS[f]}</SelectItem>)}
                            <SelectItem value="custom">Custom Field…</SelectItem>
                            <SelectItem value="skip">Don't Import</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {m.target === 'custom' && (
                        <Input
                          value={m.custom_name || ''}
                          onChange={(e) => setCustomName(h, e.target.value)}
                          placeholder="Custom field name (e.g. Lead Source)"
                          className="h-8 text-sm"
                          data-testid={`map-custom-${h}`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1" onClick={() => { setStep('upload'); setError(''); }}>
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <Button className="flex-1 bg-pink-600 hover:bg-pink-700" onClick={runAnalyze} disabled={busy} data-testid="map-continue-btn">
                  {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Continue
                </Button>
              </div>
            </div>
          )}

          {/* STEP: REVIEW */}
          {step === 'review' && analysis && (
            <div className="space-y-4">
              <div>
                <h3 className="font-outfit font-bold text-lg">Review & Preview</h3>
                <p className="text-sm text-muted-foreground">{session.total} contacts found</p>
              </div>

              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-900 p-2" data-testid="stat-new">
                  <p className="text-lg font-bold text-emerald-600">{analysis.new}</p>
                  <p className="text-[11px] text-muted-foreground">ready</p>
                </div>
                <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900 p-2" data-testid="stat-dup">
                  <p className="text-lg font-bold text-amber-600">{analysis.duplicates}</p>
                  <p className="text-[11px] text-muted-foreground">duplicates</p>
                </div>
                <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900 p-2" data-testid="stat-invalid">
                  <p className="text-lg font-bold text-red-600">{analysis.invalid}</p>
                  <p className="text-[11px] text-muted-foreground">invalid</p>
                </div>
              </div>

              {/* Preview table */}
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">
                  Preview: {analysis.preview.length} of {session.total} contacts
                </p>
                <ScrollArea className="h-48 rounded-xl border">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-100 dark:bg-slate-800">
                      <tr className="text-left">
                        <th className="p-2 font-semibold">Name</th>
                        <th className="p-2 font-semibold">Phone</th>
                        <th className="p-2 font-semibold">Email</th>
                      </tr>
                    </thead>
                    <tbody data-testid="import-preview-body">
                      {(analysis.preview || []).map((c, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-2">
                            {c.name}
                            {c.is_duplicate && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-700">dup</span>}
                          </td>
                          <td className="p-2">{c.phone || '—'}</td>
                          <td className="p-2 truncate max-w-[120px]">{c.email || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              </div>

              {/* Invalid review */}
              {analysis.invalid > 0 && (
                <details className="rounded-xl border p-3" data-testid="invalid-review">
                  <summary className="text-sm font-medium cursor-pointer text-red-600">Review {analysis.invalid} invalid row{analysis.invalid !== 1 ? 's' : ''}</summary>
                  <ul className="mt-2 space-y-1 text-xs text-muted-foreground max-h-32 overflow-y-auto">
                    {(analysis.invalid_sample || []).map((r, i) => (
                      <li key={i}>Row {r.row}: {r.reason}</li>
                    ))}
                  </ul>
                </details>
              )}

              {/* Duplicate handling */}
              <div className="space-y-2">
                <Label className="text-sm">If a contact already exists</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { v: 'skip', label: 'Skip', icon: SkipForward },
                    { v: 'update', label: 'Update', icon: UserCheck },
                    { v: 'create', label: 'Create New', icon: UserPlus },
                  ].map(({ v, label, icon: Icon }) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setDuplicateRule(v)}
                      className={`rounded-xl border p-2 flex flex-col items-center gap-1 text-xs transition-colors ${duplicateRule === v ? 'border-pink-500 bg-pink-50 dark:bg-pink-900/20 text-pink-700 dark:text-pink-300 font-semibold' : 'border-slate-200 dark:border-slate-700'}`}
                      data-testid={`dup-rule-${v}`}
                    >
                      <Icon className="w-4 h-4" /> {label}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {duplicateRule === 'skip' && "Duplicates won't be imported (recommended)."}
                  {duplicateRule === 'update' && 'Existing contacts get new info added — nothing is erased.'}
                  {duplicateRule === 'create' && 'Duplicates import as separate contacts.'}
                </p>
              </div>

              {/* Source, Tag, Folder */}
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <Label className="text-sm">Source</Label>
                  <Select value={sourceSel} onValueChange={setSourceSel}>
                    <SelectTrigger data-testid="import-source-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__default__">CSV Import (default)</SelectItem>
                      {SOURCE_PRESETS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      <SelectItem value="__custom__">Custom…</SelectItem>
                    </SelectContent>
                  </Select>
                  {sourceSel === '__custom__' && (
                    <Input className="mt-2" placeholder="Enter a source" value={customSource} onChange={(e) => setCustomSource(e.target.value)} data-testid="import-source-custom" />
                  )}
                </div>
                <div>
                  <Label className="text-sm">Add tag to all imported contacts</Label>
                  <Input placeholder="e.g. Project Broadcast" value={addTag} onChange={(e) => setAddTag(e.target.value)} data-testid="import-add-tag" />
                </div>
                <div>
                  <Label className="text-sm">Add to folder</Label>
                  <Select value={folder} onValueChange={setFolder}>
                    <SelectTrigger data-testid="import-folder-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(['General', ...folders.map((f) => f.name)].filter((n, i, a) => a.indexOf(n) === i)).map((name) => (
                        <SelectItem key={name} value={name}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1" onClick={() => setStep('map')}>
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <Button className="flex-1 bg-pink-600 hover:bg-pink-700" onClick={() => setStep('confirm')} data-testid="review-continue-btn">
                  Continue <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* STEP: CONFIRM */}
          {step === 'confirm' && analysis && (
            <div className="space-y-4">
              <div className="text-center">
                <div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-3">
                  <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="font-outfit font-bold text-lg">Ready to Import</h3>
              </div>
              <div className="rounded-xl border divide-y text-sm">
                <Row label="Contacts found" value={session.total} />
                <Row label="New contacts" value={duplicateRule === 'create' ? analysis.new + analysis.duplicates : analysis.new} />
                <Row label="Existing / duplicates" value={analysis.duplicates} />
                <Row label="Invalid (skipped)" value={analysis.invalid} />
                <Row label="Duplicate rule" value={duplicateRule === 'skip' ? 'Skip duplicates' : duplicateRule === 'update' ? 'Update existing' : 'Create new'} />
                <Row label="Source" value={resolvedSource || 'CSV Import'} />
                <Row label="Tag" value={addTag.trim() || '—'} />
                <Row label="Folder" value={folder} />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setStep('review')}>
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <Button className="flex-1 bg-pink-600 hover:bg-pink-700" onClick={runImport} data-testid="confirm-import-btn">
                  <Upload className="w-4 h-4 mr-1" /> Import Contacts
                </Button>
              </div>
            </div>
          )}

          {/* STEP: PROGRESS */}
          {step === 'progress' && job && (
            <div className="py-6 text-center space-y-4">
              {job.status === 'failed' ? (
                <AlertTriangle className="w-10 h-10 mx-auto text-red-500" />
              ) : (
                <Loader2 className="w-10 h-10 mx-auto animate-spin text-pink-600" />
              )}
              <div>
                <p className="font-medium">
                  {job.status === 'failed' ? 'Import paused' : 'Importing your contacts…'}
                </p>
                <p className="text-2xl font-bold text-pink-600 mt-1" data-testid="import-progress-count">
                  {(job.processed || 0).toLocaleString()} / {(job.total || 0).toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">{progressPct}% complete</p>
              </div>
              <div className="h-2 w-full rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                <div className="h-full bg-pink-500 transition-all" style={{ width: `${progressPct}%` }} data-testid="import-progress-bar" />
              </div>

              <div className="grid grid-cols-4 gap-2 text-center text-xs">
                <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 p-2" data-testid="progress-added">
                  <p className="font-bold text-emerald-600">{job.imported || 0}</p>
                  <p className="text-[10px] text-muted-foreground">added</p>
                </div>
                <div className="rounded-lg bg-sky-50 dark:bg-sky-900/20 p-2" data-testid="progress-updated">
                  <p className="font-bold text-sky-600">{job.updated || 0}</p>
                  <p className="text-[10px] text-muted-foreground">updated</p>
                </div>
                <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-2" data-testid="progress-skipped">
                  <p className="font-bold text-amber-600">{job.skipped || 0}</p>
                  <p className="text-[10px] text-muted-foreground">dup skipped</p>
                </div>
                <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-2" data-testid="progress-invalid">
                  <p className="font-bold text-red-600">{(job.invalid || 0) + (job.failed || 0)}</p>
                  <p className="text-[10px] text-muted-foreground">skipped/err</p>
                </div>
              </div>

              {job.status === 'failed' ? (
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => close(false)} data-testid="import-close-failed-btn">
                    Close
                  </Button>
                  <Button className="flex-1 bg-pink-600 hover:bg-pink-700" onClick={resumeImport} data-testid="import-resume-btn">
                    <RefreshCw className="w-4 h-4 mr-1" /> Resume Import
                  </Button>
                </div>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                    You can close this — the import keeps running in the background.
                  </p>
                  <Button variant="outline" className="w-full" onClick={() => close(false)} data-testid="import-run-background-btn">
                    Close &amp; keep importing
                  </Button>
                </>
              )}
            </div>
          )}

          {/* STEP: DONE */}
          {step === 'done' && job && (
            <div className="py-6 text-center space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <CheckCircle2 className="w-9 h-9 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-outfit font-bold text-xl">Import Complete!</h3>
                <div className="text-sm text-muted-foreground mt-2 space-y-0.5">
                  <p><b className="text-foreground">{(job.total || 0).toLocaleString()}</b> contacts processed.</p>
                  <p><b className="text-emerald-600">{job.imported || 0}</b> contacts added.</p>
                  {job.updated > 0 && <p><b className="text-sky-600">{job.updated}</b> existing contacts updated.</p>}
                  {job.skipped > 0 && <p><b className="text-amber-600">{job.skipped}</b> duplicates skipped.</p>}
                  {((job.invalid || 0) + (job.failed || 0)) > 0 && (
                    <p><b className="text-red-600">{(job.invalid || 0) + (job.failed || 0)}</b> records skipped (invalid or failed).</p>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={reset} data-testid="import-another-btn">
                  <RefreshCw className="w-4 h-4 mr-1" /> Import Another File
                </Button>
                <Button className="flex-1 bg-pink-600 hover:bg-pink-700" onClick={() => close(false)} data-testid="import-view-contacts-btn">
                  <Users className="w-4 h-4 mr-1" /> View Contacts
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between px-3 py-2">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);

export default ContactImportDialog;
